import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    MobileNotification,
    MobileNotificationDetail,
    MobileNotificationState,
    PhpClientPaginatedResponse,
} from "../../../types";
import { getNotificationDetail, getNotifications } from "./api";

const initialState: MobileNotificationState = {
    isNotificationsLoading: false,
    notifications: [],

    isNotificationDetailLoading: false,
    fetchedNotificationDetails: undefined,
};

// Helper function to parse JSON safely
const parseJSONSafely = (data: any) => {
    try {
        return typeof data === "string" ? JSON.parse(data) : data;
    } catch (error) {
        console.error("Error parsing JSON:", error);
        return data;
    }
};

export const mobileNotificationSlice = createSlice({
    name: "mobileNotifications",
    initialState: initialState,
    reducers: {
        setMobileNotifications: (state, { payload }) => {
            state.notifications = payload;
        },

        clearMobileNotifications: (state) => {
            state.notifications = [];
        },

        clearFetchedNotificationDetails: (state) => {
            state.fetchedNotificationDetails = undefined;
        },
    },
    extraReducers: {
        // get notifications
        [getNotifications.pending.type]: (state) => {
            state.isNotificationsLoading = true;
        },

        [getNotifications.fulfilled.type]: (
            state,
            action: PayloadAction<PhpClientPaginatedResponse<MobileNotification>>
        ) => {
            const { data, meta } = action.payload;
            state.isNotificationsLoading = false;

            const notificationMap = new Map(state.notifications.map((n) => [n.notification_id, n]));

            for (const incoming of data) {
                if (!incoming.notification_id) continue;

                if (notificationMap.has(incoming.notification_id)) {
                    // Update existing notification
                    notificationMap.set(incoming.notification_id, {
                        ...notificationMap.get(incoming.notification_id),
                        ...incoming, // merges fields from new data into the existing one
                    });
                } else {
                    // Add new notification
                    notificationMap.set(incoming.notification_id, incoming);
                }
            }

            state.notifications = Array.from(notificationMap.values()).sort((a, b) => {
                const dateA = a.notification_date_time ? new Date(a.notification_date_time).getTime() : 0;
                const dateB = b.notification_date_time ? new Date(b.notification_date_time).getTime() : 0;
                return dateB - dateA; // Descending order
            });
            state.notificationsMeta = meta;
        },

        [getNotifications.rejected.type]: (state) => {
            state.isNotificationsLoading = false;
        },

        // get notification details
        [getNotificationDetail.pending.type]: (state) => {
            state.isNotificationDetailLoading = true;
        },

        [getNotificationDetail.fulfilled.type]: (state, { payload }) => {
            state.isNotificationDetailLoading = false;
            const parsedNotificationDetail: MobileNotificationDetail = {
                ...payload,
                notification_param_json: parseJSONSafely(payload.notification_param_json),
                alert_json: parseJSONSafely(payload.alert_json),
            };
            state.fetchedNotificationDetails = parsedNotificationDetail;
        },

        [getNotificationDetail.rejected.type]: (state) => {
            state.isNotificationDetailLoading = false;
        },
    },
});

export const {} = mobileNotificationSlice.actions;
export default mobileNotificationSlice.reducer;
